/**
 * Recipe detail provider.
 *
 * Exposes recipe data, derived domain constraints (archived gating), and thin mutation wrappers.
 */
import {
  createContext,
  useContext as useReactContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import type { Zero } from "@rocicorp/zero";
import { Recipe } from "@corpuscomplete/core/nutrition/recipe";
import { queries } from "@corpuscomplete/core/zero";

export namespace RecipeDetail {
  type Context = {
    recipeId: string;
  };

  const DataContext = createContext<Context | null>(null);

  type DataProviderProps = PropsWithChildren<{
    recipeId: string;
  }>;

  export function DataProvider({ children, recipeId }: DataProviderProps) {
    return <DataContext value={{ recipeId }}>{children}</DataContext>;
  }

  function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error("RecipeDetail.useContext must be used within RecipeDetail.DataProvider");
    }
    return context;
  }

  export function useDetails(): {
    recipe: Recipe.Info | undefined;
    isLoading: boolean;
    constraints: DomainConstraints;
  } {
    const { recipeId } = useContext();
    const [row, details] = useQuery(queries.recipe.byId({ id: recipeId }), { ttl: "10s" });
    const recipe = useMemo(() => (row ? Recipe.asInfo(row) : undefined), [row]);

    return useMemo(() => {
      const isArchived = Boolean(recipe?.meta.isArchived);

      return {
        recipe,
        isLoading: details.type === "unknown",
        constraints: {
          canEdit: !isArchived,
          canDelete: !isArchived,
          canLogToFoodLog: !isArchived,
          canAddToMealPlan: !isArchived,
        },
      };
    }, [details.type, recipe]);
  }

  export type DomainConstraints = {
    canEdit: boolean;
    canDelete: boolean;
    canLogToFoodLog: boolean;
    canAddToMealPlan: boolean;
  };

  export function preload(zero: Zero, recipeId: string): void {
    void zero.run(queries.recipe.byId({ id: recipeId }));
  }
}
